import { queriesInternalAccounts, queriesPayrollRuns } from "@query";
import { WSEmptyState, WSGrid, WSList } from "@wingspanhq/fe-component-library";
import { PayrollRunType } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { useHistory } from "react-router-dom";

import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { useGoToTransfer } from "../../../Transfer/paths";
import { PATH_WALLET } from "../../../Wallet/paths";
import { PayrollRunCard } from "../../components/PayrollRunCard";
import { RecentHistoryWidget } from "../../components/RecentHistoryWidget";
import { WalletBalanceCard } from "../../components/WalletBalanceCard";
import { PAYROLL_RUNS_ACTIVE_STATUSES } from "../../constants";
import {
  PayrollRunsYear,
  routeHelperPayrollRunsDashboardActivePayrolls,
  routeHelperPayrollRunsDashboardHistory,
  routeHelperPayrollRunsDetails
} from "../../paths";
import { selectorPayrollRunHistoryItems } from "../../selectors/selectorPayrollRunHistoryItems";
import { selectorPayrollRunsWalletBalance } from "../../selectors/selectorPayrollRunsWalletBalance";
import { getPayrollRunYearDateRange } from "../../utils";

const RECENT_HISTORY_LIMIT = 5;

export const RoutePayrollRunsActivePayrolls: React.FC = () => {
  const history = useHistory();
  const {
    params: { year: yearParam }
  } = routeHelperPayrollRunsDashboardActivePayrolls.useData();
  const year = yearParam as PayrollRunsYear;

  const goToDetails = routeHelperPayrollRunsDetails.useGoTo();
  const goToHistory = routeHelperPayrollRunsDashboardHistory.useGoTo();
  const goToTransfer = useGoToTransfer();

  const queryPayrolls = queriesPayrollRuns.listPayrollRuns.useQueryAll({
    filter: {
      type: { eq: PayrollRunType.EMPLOYEE },
      payDate: getPayrollRunYearDateRange(year),
      status: { anyOf: PAYROLL_RUNS_ACTIVE_STATUSES }
    }
  });
  const payrollRuns = queryPayrolls.flatData ?? [];

  // Timeline across the active runs — the only history the v3 API exposes today.
  const historyItems = selectorPayrollRunHistoryItems({
    payrollRuns,
    limit: RECENT_HISTORY_LIMIT
  });

  // Funding wallet + balance for the sidebar card.
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const fundingAccount = queryInternalAccounts.data?.data?.find(
    selectorActiveBankingAccount
  );
  const queryBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: fundingAccount?.id ?? "" },
      { enabled: !!fundingAccount?.id }
    );
  const walletBalance = selectorPayrollRunsWalletBalance({
    internalAccount: fundingAccount,
    balance: queryBalance.data
  });

  return (
    <WSGrid gutter="L" mt="L">
      <WSGrid.Item span={{ s: "12", l: "8" }}>
        {!queryPayrolls.isInitialLoading && payrollRuns.length === 0 ? (
          <WSEmptyState
            bgColor="gray"
            description="Active payroll runs for the selected year will appear here."
            hasBorder
            title="No active payrolls"
            type="documents"
          />
        ) : (
          <WSList gap="L">
            {payrollRuns.map(payrollRun => (
              <PayrollRunCard
                key={payrollRun.id}
                goToDetails={() => {
                  goToDetails({ year, payrollRunId: payrollRun.id ?? "" });
                }}
                payrollRun={payrollRun}
              />
            ))}
          </WSList>
        )}
      </WSGrid.Item>

      <WSGrid.Item span={{ s: "12", l: "4" }}>
        <WSList gap="L">
          <WalletBalanceCard
            balance={walletBalance}
            onManageAccount={() => history.push(PATH_WALLET)}
            onTopUp={() => goToTransfer({ balance: "wallet", type: "deposit" })}
          />
          <RecentHistoryWidget
            items={historyItems}
            onViewAll={() => goToHistory({ year })}
          />
        </WSList>
      </WSGrid.Item>
    </WSGrid>
  );
};
